import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import TextareaAutosize from 'react-textarea-autosize';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useFilter } from '../FilterContext';

function MainFilter({ updateShowTrends, categories, intents, agents, initiationMethods, queues, minorIntents }) {
  const { filters, setFilters } = useFilter();
  const [callIdInput, setCallIdInput] = useState(filters.callId || '');
  const [showTrends, setShowTrends] = useState(false);

  // Local state for date pickers
  const [localDateStart, setLocalDateStart] = useState(new Date());
  const [localDateEnd, setLocalDateEnd] = useState(new Date());
  const [localDateRange, setLocalDateRange] = useState(filters.dateRange || { value: 'today', label: 'Today' });
  const [showDatePickerPopup, setShowDatePickerPopup] = useState(false);

  const dateOptions = useMemo(
    () => [
      { value: 'today', label: 'Today' },
      { value: 'thisWeek', label: 'This Week' },
      { value: 'thisMonth', label: 'This Month' },
      { value: 'thisYear', label: 'This Year' },
      { value: 'all', label: 'All' },
      { value: 'custom', label: 'Custom' },
    ],
    []
  );

  const findOptionByValue = (value) => {
    return agents.find((agent) => agent.value === value) || null;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleChange = useCallback(
    (selectedOptions, { name }) => {
      if (name === 'dateRange') {
        if (selectedOptions.value === 'custom') {
          setLocalDateRange(selectedOptions);
          setShowDatePickerPopup(true);
        } else {
          setFilters((prev) => ({
            ...prev,
            dateRange: selectedOptions,
            dateStart: null,
            dateEnd: null,
          }));
          setLocalDateRange(selectedOptions);
        }
      } else {
        setFilters((prev = {}) => {
          const newFilters = {
            ...prev,
            [name]: selectedOptions,
          };
          return newFilters;
        });
      }
    },
    [setFilters]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev = {}) => {
        if (prev.callId === callIdInput) {
          return prev; // No change, avoid re-render
        }
        return { ...prev, callId: callIdInput };
      });
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [callIdInput, setFilters]);

  const clearFilter = useCallback(
    (name) => {
      setFilters((prev = {}) => {
        const newFilters = {
          ...prev,
          [name]: name === 'callId' ? '' : name === 'agent' ? null : [],
        };
        return newFilters;
      });
      if (name === 'callId') {
        setCallIdInput('');
      }
    },
    [setFilters]
  );

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      height: 'auto',
      backgroundColor:
        state.selectProps.name === 'dateRange' &&
        filters.dateRange &&
        filters.dateRange.value === 'custom'
          ? '#e6f7ff' // Highlight when custom date range is selected
          : 'white',
    }),
    option: (provided) => ({
      ...provided,
      display: 'flex',
      justifyContent: 'space-between',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e2e8f0',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '14px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      flexWrap: 'nowrap',
      overflowX: 'auto',
      maxHeight: '100px',
    }),
  };

  const formatOptionLabel = ({ label, count }, { context }) => {
    if (context === 'menu') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{label}</span>
          {count !== undefined && (
            <span style={{ color: '#aaa', paddingLeft: '5px' }}>({count})</span>
          )}
        </div>
      );
    } else {
      return label;
    }
  };

  const handleExcludeWeekends = (event) => {
    setFilters((prev) => ({ ...prev, excludeWeekends: event.target.checked }));
  };

  const handleShowTrends = () => {
    setShowTrends(!showTrends);
    updateShowTrends(!showTrends);
  };

  useEffect(() => {
    if (filters.dateStart) {
      setLocalDateStart(new Date(filters.dateStart));
    }
    if (filters.dateEnd) {
      setLocalDateEnd(new Date(filters.dateEnd));
    }
  }, [filters.dateStart, filters.dateEnd]);

  return (
    <div className="mb-4 p-4 bg-gray-100 rounded-lg">
      <div className="flex space-x-4 mb-4">
        <div className="flex-1 relative">
          <TextareaAutosize
            name="callId"
            value={callIdInput}
            onChange={(e) => setCallIdInput(e.target.value)}
            placeholder="Call ID"
            className="w-full p-2 rounded resize-none"
            minRows={1}
            maxRows={3}
          />
          {callIdInput && (
            <button
              onClick={() => clearFilter('callId')}
              className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          )}
        </div>
        <div className="flex-1 relative">
          <Select
            name="agent"
            value={filters.agent ? findOptionByValue(filters.agent.value) : null}
            onChange={(option) => handleChange(option, { name: 'agent' })}
            options={agents}
            isClearable
            placeholder="Select Agent"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="category"
            isMulti
            value={filters.category}
            onChange={(option) => handleChange(option, { name: 'category' })}
            options={categories}
            isClearable
            placeholder="Select Category"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="intent"
            isMulti
            value={filters.intent}
            onChange={(option) => handleChange(option, { name: 'intent' })}
            options={intents}
            isClearable
            placeholder="Select Intent"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
      </div>
      <div className="flex justify-end items-center space-x-4 mt-0">
        <div className="flex-1 relative">
          <Select
            name="dateRange"
            value={
              filters.dateRange && filters.dateRange.value === 'custom' && filters.dateStart && filters.dateEnd
                ? {
                    value: 'custom',
                    label: `${formatDate(filters.dateStart)} : ${formatDate(filters.dateEnd)}`,
                  }
                : filters.dateRange
            }
            onChange={(option) => handleChange(option, { name: 'dateRange' })}
            options={dateOptions}
            styles={customStyles}
            onFocus={() => {
              if (filters.dateRange && filters.dateRange.value === 'custom') {
                setShowDatePickerPopup(true);
              }
            }}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="queue"
            value={filters.queue}
            onChange={(option) => handleChange(option, { name: 'queue' })}
            options={queues}
            isClearable
            placeholder="Select Queue"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="initiationMethod"
            value={filters.initiationMethod}
            onChange={(option) => handleChange(option, { name: 'initiationMethod' })}
            options={initiationMethods}
            isClearable
            placeholder="Select Call Origination"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
        <div className="flex-1 relative">
          <Select
            name="minorIntents"
            isMulti
            value={filters.minorIntents}
            onChange={(option) => handleChange(option, { name: 'minorIntents' })}
            options={minorIntents}
            isClearable
            placeholder="Select Minor Intent"
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: 'rgb(129 140 248)',
              },
            })}
          />
        </div>
      </div>
      <div className="flex justify-end mt-3 ml-1">
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.excludeWeekends}
              onChange={handleExcludeWeekends}
              className="form-checkbox h-5 w-5 text-indigo-600 accent-indigo-600"
            />
            <span className="text-sm">Exclude weekends</span>
          </label>
          <button
            onClick={handleShowTrends}
            className="px-4 py-1 bg-indigo-800 text-white rounded hover:bg-indigo-600 text-sm"
          >
            {showTrends ? 'Hide Trends' : 'Show Trends'}
          </button>
        </div>
      </div>

      {/* Date Picker Popup */}
      {showDatePickerPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg">
            <h2 className="text-xl mb-4">Select Date Range</h2>
            <div className="mb-4">
              <Select
                name="localDateRange"
                value={localDateRange}
                onChange={(option) => {
                  if (option.value !== 'custom') {
                    setFilters((prev) => ({
                      ...prev,
                      dateRange: option,
                      dateStart: null,
                      dateEnd: null,
                    }));
                    setLocalDateRange(option);
                    setShowDatePickerPopup(false);
                  } else {
                    setLocalDateRange(option);
                  }
                }}
                options={dateOptions}
                styles={customStyles}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: 'rgb(129 140 248)',
                  },
                })}
              />
            </div>
            <div className="flex space-x-4">
  <div>
    <label className="block mb-2">Start Date</label>
    <DatePicker
      selected={localDateStart}
      onChange={(date) => {
        setLocalDateStart(date);
        if (date > localDateEnd) {
          setLocalDateEnd(date);
        }
      }}
      selectsStart
      startDate={localDateStart}
      endDate={localDateEnd}
      maxDate={localDateEnd}
      dateFormat="yyyy-MM-dd"
      inline
    />
  </div>
  <div>
    <label className="block mb-2">End Date</label>
    <DatePicker
      selected={localDateEnd}
      onChange={(date) => {
        setLocalDateEnd(date);
        if (date < localDateStart) {
          setLocalDateStart(date);
        }
      }}
      selectsEnd
      startDate={localDateStart}
      endDate={localDateEnd}
      minDate={localDateStart}
      dateFormat="yyyy-MM-dd"
      inline
    />
  </div>
</div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                onClick={() => {
                  setShowDatePickerPopup(false);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                onClick={() => {
                  if (localDateEnd >= localDateStart) {
                    setFilters((prev) => ({
                      ...prev,
                      dateStart: localDateStart,
                      dateEnd: localDateEnd,
                      dateRange: {
                        value: 'custom',
                        label: `${formatDate(localDateStart)} : ${formatDate(localDateEnd)}`,
                      },
                    }));
                    setShowDatePickerPopup(false);
                  } else {
                    alert('End date cannot be earlier than start date.');
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainFilter;
